/**
 * Hook globale per sapere se l'organizzazione corrente può fare scritture.
 *
 * Specchio lato client della funzione DB `org_can_write(uuid)`:
 *  - FALSE se piano è `cancelled` o `past_due` → app in sola lettura
 *  - TRUE altrimenti
 *
 * Il vero gate è lato DB via RLS (policy RESTRICTIVE che usa org_can_write).
 * Questo hook serve a:
 *  - mostrare/nascondere il banner "subscription scaduta"
 *  - disabilitare visivamente bottoni Save/Edit/Nuovo prima ancora del tentativo
 *  - condizionare onClick handler per mostrare un toast CTA invece di chiamare
 *    una mutation che verrebbe rifiutata dal DB
 *
 * Niente fetch nuovi: legge da `useOrgPlan` che è già cached in OrganizationContext.
 */
import { useOrgPlan, type PlanCode } from '@/hooks/useOrgPlan';

const BLOCKED_PLANS: ReadonlySet<PlanCode> = new Set(['cancelled', 'past_due', 'trial_expired']);

export interface CanWriteState {
  /** TRUE se l'org può scrivere (piano attivo). */
  canWrite: boolean;
  /** TRUE se l'org è bloccata (cancelled o past_due). */
  blocked: boolean;
  /** Codice piano corrente. */
  plan: PlanCode;
  /** Motivo human-readable se bloccata. Null se canWrite. */
  blockedReason: string | null;
}

export function useCanWrite(): CanWriteState {
  const { code } = useOrgPlan();
  const blocked = BLOCKED_PLANS.has(code);
  let blockedReason: string | null = null;
  if (blocked) {
    if (code === 'past_due') {
      blockedReason = 'Pagamento sospeso: aggiorna il metodo di pagamento per riprendere.';
    } else if (code === 'trial_expired') {
      blockedReason = 'Periodo di prova terminato: sottoscrivi un piano per continuare a lavorare.';
    } else {
      blockedReason = 'Abbonamento scaduto o cancellato: riattiva per riprendere a lavorare.';
    }
  }

  return {
    canWrite: !blocked,
    blocked,
    plan: code,
    blockedReason,
  };
}
